import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

interface GlobalShortcutsState {
  paletteOpen: boolean;
  setPaletteOpen: (open: boolean) => void;
  shortcutsOpen: boolean;
  setShortcutsOpen: (open: boolean) => void;
}

/**
 * Wires up global keyboard shortcuts for the manager app.
 * - ⌘K / Ctrl+K → command palette
 * - ?           → shortcuts dialog
 * - g + [d|c|t|s|a] → goto navigation
 */
export const useGlobalShortcuts = (): GlobalShortcutsState => {
  const navigate = useNavigate();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const lastKey = useRef<{ key: string; time: number } | null>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const inField =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target?.isContentEditable;

      // ⌘K / Ctrl+K → palette (works even in inputs)
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen(true);
        return;
      }

      if (inField) return;

      // ? → shortcuts
      if (e.key === "?" && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        setShortcutsOpen(true);
        return;
      }

      // g + x sequences
      const now = Date.now();
      if (lastKey.current && lastKey.current.key === "g" && now - lastKey.current.time < 1200) {
        const map: Record<string, string> = {
          d: "/dashboard",
          c: "/cycles",
          t: "/templates",
          s: "/schedules",
          a: "/analytics",
        };
        const path = map[e.key.toLowerCase()];
        if (path) {
          e.preventDefault();
          navigate(path);
          lastKey.current = null;
          return;
        }
      }
      lastKey.current = { key: e.key.toLowerCase(), time: now };
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [navigate]);

  return { paletteOpen, setPaletteOpen, shortcutsOpen, setShortcutsOpen };
};
